import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotification } from 'src/contexts/NotificationContext';
import BlankPrimaryLayout from 'src/layouts/BlankPrimaryLayout';
import Spinner from 'src/components/common/designSystem/Spinner';
import {
  FamilyMember,
  useCheckFamilyCode,
  useJoinFamily,
} from 'src/queries/family_code';
import LayoutWithHeader from 'src/layouts/LayoutWithHeader';
import RadioList from 'src/components/common/designSystem/RadioList';
import Footer from 'src/components/common/designSystem/Footer';
import Button from 'src/components/common/designSystem/Button';
import { AutoFloatingPanel } from 'src/components/common/AutoFloatingPanel';
import { UserType } from 'src/types/user.types';
import Header from 'src/components/Header/Header';
import LoginPanel from 'src/components/Login/LoginPanel';

// This page is shown when a child enters a valid family code
// It fetches the family members and shows a selection screen
const SelectFamilyUser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showLoginPanel, setShowLoginPanel] = useState(false);
  // Get the family code from URL params
  const familyCode = searchParams.get('family_code');
  const userType = searchParams.get('user_type');
  const isParent = userType === UserType.parent;

  // Verify the family code
  const { mutateAsync: checkFamilyCode, isLoading: isCheckingCode } =
    useCheckFamilyCode();

  // Join family hook
  const { mutateAsync: joinFamily, isLoading: isJoining } = useJoinFamily();

  // If no family code is provided, redirect to select type page
  if (!familyCode) {
    navigate('/onboarding/select-type');
    return null;
  }

  // Validate the code once when the component mounts
  useEffect(() => {
    const validateCode = async () => {
      try {
        const result = await checkFamilyCode({ join_code: familyCode });

        if (!result.exists) {
          showError({
            message: t('family.code.invalid'),
          });
          navigate('/onboarding/select-type');
          return;
        }

        // Store the family members from the API response
        if (result.members) {
          setFamilyMembers(result.members);
        }
      } catch (error) {
        showError({
          error,
          message: t('family.code.invalid'),
        });
        navigate('/onboarding/select-type');
      }
    };

    validateCode();
  }, [familyCode, checkFamilyCode, navigate, showError, t]);

  const handleJoinFamily = async () => {
    if (!selectedUserId || !familyCode) return;

    try {
      // Join the family and log in with the selected user ID
      await joinFamily({
        join_code: familyCode,
        user_id: selectedUserId,
      });

      showSuccess({
        message: t('family.code.success'),
      });

      // Navigate to home page after successful login
      navigate('/');
    } catch (error) {
      showError({
        error,
        message: t('family.code.error.join'),
      });
    }
  };

  const handleHelperButtonClick = () => {
    const userType = searchParams.get('user_type');
    const isParent = userType === UserType.parent;
    if (isParent) {
      setShowLoginPanel(true);
      return;
    }
    setShowHelpPanel(true);
  };

  const handleAfterSocialLogin = () => {
    setShowLoginPanel(false);
    const newParams = new URLSearchParams(searchParams);
    navigate(`/onboarding/parent/firstname?${newParams.toString()}`, {
      replace: true,
    });
  };
  const handleEmailClick = () => {
    setShowLoginPanel(false);
    navigate(`/login/email?${searchParams.toString()}`);
  };

  // Show loading when checking code
  if (isCheckingCode) {
    return (
      <BlankPrimaryLayout>
        <div className="flex-1 flex items-center justify-center">
          <Spinner className="w-16 h-16" />
        </div>
      </BlankPrimaryLayout>
    );
  }

  // Filter members to only show children
  const filteredMembers = userType
    ? familyMembers.filter(member => member.user_type === userType)
    : familyMembers;

  // Show the family user selection
  return (
    <LayoutWithHeader header={<Header backArrow />}>
      <div className="flex-1 flex flex-col items-center justify-between w-full">
        <div className={'text-base font-bold'}>
          {isParent
            ? t('family.code.selectUser.parent.title')
            : t('family.code.selectUser.child.title')}
        </div>
        <div className="flex flex-col justify-center w-full">
          {filteredMembers.length > 0 ? (
            <RadioList
              options={filteredMembers.map(member => ({
                type: 'value',
                value: member.id,
                label: member.firstname,
              }))}
              selected={selectedUserId || undefined}
              onSelect={value => setSelectedUserId(value)}
            />
          ) : (
            <div className="text-center">{t('family.code.noChildren')}</div>
          )}

          {/* Help button for children who can't find their profile */}
          <Button
            type={'link'}
            className={'!text-structural-darkest'}
            data-testid="profile-not-found-help"
            onClick={handleHelperButtonClick}
          >
            {t('family.code.selectUser.profileNotFound.buttonText')}
          </Button>
        </div>
        <div />
      </div>
      <Footer>
        <Button
          data-testid="btn-select-user"
          type="primary"
          disabled={!selectedUserId}
          loading={isJoining}
          onClick={handleJoinFamily}
        >
          {t('global.continue')}
        </Button>
      </Footer>

      {/* Help Floating Panel */}
      <AutoFloatingPanel
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
        data-testid="profile-not-found-panel"
      >
        <div className="p-4 px-6 flex flex-col items-center gap-4">
          <div className="font-bold text-balance text-2xl">
            {t('family.code.selectUser.profileNotFound.title')}
          </div>
          <p className="text-center">
            {t('family.code.selectUser.profileNotFound.description')}
          </p>
          <Button
            type="primary"
            data-testid="profile-not-found-ok"
            onClick={() => setShowHelpPanel(false)}
          >
            {t('global.understand')}
          </Button>
        </div>
      </AutoFloatingPanel>

      {/* Login Floating Panel */}
      <AutoFloatingPanel
        isOpen={showLoginPanel}
        onClose={() => setShowLoginPanel(false)}
        data-testid="login-panel"
      >
        <LoginPanel
          title={t('login.createAccount')}
          className="p-2"
          onLogin={handleAfterSocialLogin}
          onEmailClick={handleEmailClick}
        />
      </AutoFloatingPanel>
    </LayoutWithHeader>
  );
};

export default SelectFamilyUser;
